import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import moment from "moment";

/** Small green / grey dot showing whether a user currently has a socket open. */
export const OnlineDot = ({ online, lastSeen, size = 10, sx = {} }) => (
  <Tooltip
    title={
      online
        ? "Online"
        : lastSeen
          ? `Last seen ${moment.unix(lastSeen).fromNow()}`
          : "Offline"
    }
  >
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "inline-block",
        flexShrink: 0,
        backgroundColor: online ? "#31A24C" : "#B0B3B8",
        boxShadow: online ? "0 0 0 2px rgba(49,162,76,0.25)" : "none",
        ...sx,
      }}
    />
  </Tooltip>
);

/** "Online" / "last seen …" caption for the conversation header. */
export const PresenceLabel = ({ online, lastSeen, ...props }) => (
  <Typography fontSize={11} color={online ? "#31A24C" : "#8a8d91"} {...props}>
    {online
      ? "Online"
      : lastSeen
        ? `Last seen ${moment.unix(lastSeen).fromNow()}`
        : "Offline"}
  </Typography>
);

/**
 * Animated "X is typing…" line. Renders nothing when nobody is typing, so it
 * can be dropped straight into a message list.
 */
export const TypingIndicator = ({ users = [] }) => {
  if (!users.length) return null;

  const names = users
    .map((user) => user?.firstName || user?.email || "Someone")
    .slice(0, 3);

  const label =
    users.length === 1
      ? `${names[0]} is typing`
      : users.length <= 3
        ? `${names.join(", ")} are typing`
        : `${names.join(", ")} and ${users.length - 3} more are typing`;

  return (
    <Box className="d-flex align-items-center px-2 py-1" sx={{ gap: "6px" }}>
      <Box className="typing-dots" sx={{ display: "flex", gap: "3px" }}>
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "#2F378D",
              opacity: 0.4,
              animation: "chatTypingBlink 1.2s infinite ease-in-out",
              animationDelay: `${index * 0.18}s`,
            }}
          />
        ))}
      </Box>
      <Typography fontSize={12} fontStyle="italic" color="#666666">
        {label}…
      </Typography>
    </Box>
  );
};

export default TypingIndicator;
